export const paths = {
  records: "/data/records.json",
  graph: "/data/entity_graph.json",
  simulations: "/data/simulations.json",
  devices: "/data/medical_device_dossiers.json",
  vulnerabilities: "/data/vulnerabilities.json",
  dashboard: "/data/dashboard.json"
};

export const kindLabels = {
  vulnerability: "Vulnerability",
  breach: "OCR breach",
  ransomware_claim: "Ransomware claim",
  indicator: "IOC",
  package_advisory: "AI repo risk",
  medical_device_advisory: "Medical device"
};

export const kindColors = {
  vulnerability: "#5f6f8f",
  breach: "#9d5649",
  ransomware_claim: "#735d79",
  indicator: "#b28a44",
  package_advisory: "#6f7a68",
  medical_device_advisory: "#6d7581",
  record: "#3f4651",
  entity: "#737a82",
  source: "#aab0b5",
  observable: "#b28a44"
};

export function displayTitle(record) {
  if (!record) return "";
  const attrs = record.attributes || {};
  const identifier = attrs.identifier || (record.vulnerabilities || [])[0];
  const bareCve = /^CVE-\d{4}-\d+$/i.test(record.title || "");
  if (record.kind === "vulnerability" && (bareCve || record.title === identifier)) {
    return vulnerabilityDisplayTitle(identifier || record.title, attrs, record.summary);
  }
  return record.title;
}

export function displaySubtitle(record) {
  if (!record) return "";
  const attrs = record.attributes || {};
  if (record.kind === "vulnerability") {
    return [attrs.vendor, attrs.product, attrs.identifier].filter(Boolean).join(" / ");
  }
  return `${kindLabels[record.kind] || record.kind} / ${record.evidence?.claim_language || "unknown"}`;
}

function vulnerabilityDisplayTitle(identifier, attrs, summary = "") {
  const product = attrs.product || "Affected product";
  const text = summary.toLowerCase();
  let issue = "security vulnerability";
  if (text.includes("remote code execution")) issue = "remote code execution";
  else if (text.includes("sql injection")) issue = "SQL injection";
  else if (text.includes("command injection")) issue = "command injection";
  else if (text.includes("backdoor")) issue = "supply-chain backdoor";
  else if (text.includes("authentication")) issue = "authentication weakness";
  return `${product} ${issue}${identifier ? ` (${identifier})` : ""}`;
}

export async function loadJson(path) {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`Unable to load ${path}`);
  return response.json();
}

export function impactScore(record) {
  const attrs = record.attributes || {};
  let score = 20;
  if (record.kind === "vulnerability") {
    if (attrs.kev) score += 32;
    if (Number(attrs.cvss) >= 9) score += 22;
    if (Number.parseFloat(attrs.epss) >= 0.9) score += 18;
  }
  if (record.kind === "breach") {
    const affected = Number(attrs["Individuals Affected"] || 0);
    if (affected > 100000) score += 35;
    if (String(attrs["Type of Breach"] || "").includes("Hacking")) score += 22;
  }
  if (record.kind === "ransomware_claim") score += 42;
  if (record.kind === "medical_device_advisory") score += 48;
  if (record.kind === "package_advisory") score += 36;
  if (record.kind === "indicator") score += Number(attrs.confidence || 0) / 3;
  if (/patient|clinical|medical device|hospital|PHI|EHR|remote access|file transfer/i.test(record.summary || "")) score += 12;
  return Math.min(100, Math.round(score));
}

export function evidenceDebtCount(records) {
  return records.filter((record) =>
    ["attacker-claimed", "needs verification", "fixture/demo", "simulation-derived"].includes(record.evidence?.claim_language)
  ).length;
}

export function evidencePosture(records) {
  const counts = records.reduce((acc, record) => {
    const label = record.evidence?.claim_language || "unknown";
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const risky = (counts["attacker-claimed"] || 0) + (counts["needs verification"] || 0);
  if (risky) return `${risky} records need careful language`;
  return `${records.length} records labeled`;
}

export function healthcareImpact(record) {
  const summary = `${record.summary || ""} ${JSON.stringify(record.attributes || {})}`;
  if (record.kind === "medical_device_advisory") return "Patient monitoring, biomed escalation, segmentation, vendor access, and downtime readiness.";
  if (/file transfer|MOVEit|claims|billing/i.test(summary)) return "PHI exchange, business associate exposure, claims operations, and extortion notification pressure.";
  if (/AI|RAG|langchain|package|dependency|repo|token|secret/i.test(summary)) return "Healthcare AI development, PHI-bearing test data, agent permissions, and toolchain integrity.";
  if (/ransomware|hospital|billing/i.test(summary)) return "Incident response, patient communications, operational continuity, and careful verification of attacker claims.";
  if (/vpn|remote access|identity/i.test(summary)) return "Remote clinical/vendor access, identity controls, lateral movement risk, and emergency patch decisions.";
  if (/email|infostealer|loader|domain|hash|ip/i.test(summary)) return "Email, proxy, DNS, EDR, and identity log review for healthcare-targeted threat activity.";
  return "Healthcare relevance exists, but needs a tighter source-backed workflow and asset mapping before public use.";
}

export function whoCares(record) {
  if (record.kind === "medical_device_advisory") return "Clinical engineering, SOC, biomed, nursing leadership, vendor management.";
  if (record.kind === "package_advisory") return "Healthcare AI engineers, platform security, appsec, data science leads.";
  if (record.kind === "breach" || record.kind === "ransomware_claim") return "Security leadership, privacy, legal, communications, vendor risk.";
  if (record.kind === "indicator") return "SOC analysts, threat hunters, email/proxy/EDR operators.";
  return "Vulnerability management, infrastructure, clinical app owners, vendor risk.";
}

export function recommendedActions(record) {
  const evidence = record.evidence?.claim_language || "unknown";
  const actions = {
    now: "Verify source posture and identify whether the affected product, vendor, or workflow exists in your environment.",
    week: "Map the signal to asset owners, detection coverage, compensating controls, and a concrete mitigation decision.",
    month: "Convert the finding into a repeatable control: patch SLA, vendor question, tabletop inject, detection, or procurement requirement."
  };
  if (record.kind === "vulnerability") {
    actions.now = "Check asset inventory for this product, prioritize KEV/high EPSS exposure, and isolate internet-facing instances if needed.";
    actions.week = "Validate patch state, compensating controls, logs, and evidence of exploitation attempts.";
    actions.month = "Add healthcare asset-class scoring to vulnerability management and vendor reviews.";
  }
  if (record.kind === "medical_device_advisory") {
    actions.now = "Ask biomed for deployed versions, network placement, remote access paths, and vendor mitigation status.";
    actions.week = "Add detection for device VLAN scanning, unusual admin access, and unexpected external connections.";
    actions.month = "Turn this into procurement language and a clinical downtime tabletop scenario.";
  }
  if (record.kind === "package_advisory") {
    actions.now = "Locate affected package usage in healthcare AI repos, notebooks, CI, and RAG/agent services.";
    actions.week = "Review secrets exposure, egress, package provenance, and agent tool permissions.";
    actions.month = "Require SBOM, dependency review, and synthetic/deidentified data boundaries for AI projects.";
  }
  if (record.kind === "ransomware_claim" && evidence.includes("attacker")) {
    actions.now = "Do not cite as confirmed. Correlate with company notices, OCR, state AG notices, or reputable reporting.";
  }
  return actions;
}

export function relatedRecords(record, records) {
  const tokens = new Set([...(record.entities || []), ...(record.vulnerabilities || []), ...(record.observables || [])].map((value) => value.toLowerCase()));
  if (!tokens.size) return [];
  return records
    .filter((other) => other.id !== record.id)
    .filter((other) => [...(other.entities || []), ...(other.vulnerabilities || []), ...(other.observables || [])].some((value) => tokens.has(value.toLowerCase())))
    .slice(0, 6);
}

export function sortedRecords(records) {
  return [...records].sort((a, b) => impactScore(b) - impactScore(a));
}

export function buildInsights(records, graph, simulations) {
  const vulnKev = records.filter((record) => record.kind === "vulnerability" && record.attributes?.kev).length;
  const attackerClaimed = records.filter((record) => record.evidence?.claim_language === "attacker-claimed").length;
  const deviceRecords = records.filter((record) => record.kind === "medical_device_advisory").length;
  const aiRecords = records.filter((record) => record.kind === "package_advisory").length;
  const patientSafetyScenarios = simulations.filter((scenario) => scenario.scorecard?.patient_safety_relevant).length;
  return [
    ["Patch priority becomes healthcare-specific", `${vulnKev} KEV-tagged vulnerabilities are connected to healthcare workflows such as file transfer, remote access, identity, or device-adjacent systems.`],
    ["Evidence quality is visible by design", `${attackerClaimed} records are attacker-claimed and should be handled as leads, not confirmed facts. This protects public trust.`],
    ["Medical-device cyber is now operational", `${deviceRecords} device advisory records map to compensating controls, detection ideas, and patient-safety escalation questions.`],
    ["AI security connects to clinical risk", `${aiRecords} AI repo/package records connect software supply-chain risk to PHI, secrets, RAG tools, and agent permissions.`],
    ["Preparedness is testable", `${patientSafetyScenarios} simulation includes patient-safety relevance, and every simulation has expected observables for detection validation.`],
    ["The graph is the product", `${graph.nodes.length} nodes and ${graph.edges.length} edges make it possible to start from a product, vendor, IOC, advisory, or source and trace decisions.`]
  ];
}

export function generateBrief(record, records) {
  const actions = recommendedActions(record);
  const related = relatedRecords(record, records);
  return `Healthcare Cyber Intelligence Brief

Signal: ${displayTitle(record)}
Type: ${kindLabels[record.kind] || record.kind}
Evidence posture: ${record.evidence?.claim_language || "unknown"} (${record.evidence?.source || record.source})
Impact score: ${impactScore(record)}/100

What this is:
${record.summary}

Who should care:
${whoCares(record)}

Healthcare impact:
${healthcareImpact(record)}

Recommended action:
24 hours: ${actions.now}
7 days: ${actions.week}
30 days: ${actions.month}

Connected signals:
${related.length ? related.map((item) => `- ${displayTitle(item)} (${kindLabels[item.kind] || item.kind})`).join("\n") : "- No direct record-level connection in current normalized data."}

Source note:
Use careful language. Do not promote fixture/demo, simulation-derived, or attacker-claimed records as confirmed incidents.`;
}
